import React, { useState, useEffect, useCallback } from 'react';
import type { PlaybookTransitionFull, PlaybookStatusFull, TransitionTriggerMode } from '../../types/playbook.types';
import GuardListPanel from './GuardListPanel';
import ValidatorListPanel from './ValidatorListPanel';
import ActionListPanel from './ActionListPanel';
import FormListPanel from './FormListPanel';
import ApprovalChainBuilder from './ApprovalChainBuilder';

interface TransitionPropertyPanelProps {
  transition: PlaybookTransitionFull;
  statuses: PlaybookStatusFull[];
  onUpdate: (transitionId: string, data: Partial<PlaybookTransitionFull>) => Promise<void>;
  onDelete: (transitionId: string) => Promise<void>;
  onRefresh: () => void;
}

const inputClass = 'w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300';
const labelClass = 'block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5';

type SubTab = 'config' | 'guards' | 'validators' | 'actions' | 'forms' | 'approval';

const TransitionPropertyPanel: React.FC<TransitionPropertyPanelProps> = ({ transition, statuses, onUpdate, onDelete, onRefresh }) => {
  const [name, setName] = useState(transition.name);
  const [slug, setSlug] = useState(transition.slug);
  const [triggerMode, setTriggerMode] = useState<TransitionTriggerMode>(transition.trigger_mode);
  const [buttonLabel, setButtonLabel] = useState(transition.button_label ?? '');
  const [buttonStyle, setButtonStyle] = useState(transition.button_style ?? 'default');
  const [confirmMessage, setConfirmMessage] = useState(transition.confirm_message ?? '');
  const [isCollaborative, setIsCollaborative] = useState(transition.is_collaborative);
  const [onRejection, setOnRejection] = useState(transition.on_rejection ?? 'block');
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('config');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setName(transition.name);
    setSlug(transition.slug);
    setTriggerMode(transition.trigger_mode);
    setButtonLabel(transition.button_label ?? '');
    setButtonStyle(transition.button_style ?? 'default');
    setConfirmMessage(transition.confirm_message ?? '');
    setIsCollaborative(transition.is_collaborative);
    setOnRejection(transition.on_rejection ?? 'block');
    setActiveSubTab('config');
    setConfirmDelete(false);
  }, [transition.id]);

  const save = useCallback(() => {
    onUpdate(transition.id, {
      name,
      slug,
      trigger_mode: triggerMode,
      button_label: buttonLabel || undefined,
      button_style: buttonStyle,
      confirm_message: confirmMessage || undefined,
      is_collaborative: isCollaborative,
      on_rejection: onRejection as any,
    });
  }, [transition.id, name, slug, triggerMode, buttonLabel, buttonStyle, confirmMessage, isCollaborative, onRejection, onUpdate]);

  const fromStatus = statuses.find((s) => s.id === transition.from_status_id);
  const toStatus = statuses.find((s) => s.id === transition.to_status_id);

  const SUB_TABS: { id: SubTab; label: string; count?: number }[] = [
    { id: 'config', label: 'Config' },
    { id: 'guards', label: 'Guards', count: transition.guards?.length },
    { id: 'validators', label: 'Validators', count: transition.validators?.length },
    { id: 'actions', label: 'Actions', count: transition.actions?.length },
    { id: 'forms', label: 'Forms', count: transition.forms?.length },
    ...(isCollaborative ? [{ id: 'approval' as SubTab, label: 'Approval' }] : []),
  ];

  return (
    <div className="space-y-3 p-3" style={{ animation: 'fadeSlideIn 200ms ease-out' }}>
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Transition Properties</h4>
        <span className="text-[9px] text-gray-400 font-mono">{transition.id.slice(0, 8)}</span>
      </div>

      {/* From → To (read-only) */}
      <div className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
        <span className="font-medium">{fromStatus?.name ?? 'Global'}</span>
        <span>→</span>
        <span className="font-medium">{toStatus?.name ?? '?'}</span>
      </div>

      {/* Sub-tab navigation */}
      <div className="flex gap-0.5 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        {SUB_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveSubTab(t.id)}
            className={`px-2 py-1 text-[10px] font-medium whitespace-nowrap transition-colors border-b-2 ${
              activeSubTab === t.id
                ? 'border-lex-accent text-lex-accent'
                : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
            }`}
          >
            {t.label}
            {t.count != null && t.count > 0 && (
              <span className="ml-0.5 text-[8px] text-gray-400">({t.count})</span>
            )}
          </button>
        ))}
      </div>

      {/* Sub-tab content */}
      {activeSubTab === 'config' && (
        <div className="space-y-3">
          <div>
            <label className={labelClass}>Name</label>
            <input type="text" className={inputClass} value={name} onChange={(e) => setName(e.target.value)} onBlur={save} />
          </div>
          <div>
            <label className={labelClass}>Slug</label>
            <input type="text" className={`${inputClass} font-mono`} value={slug} onChange={(e) => setSlug(e.target.value)} onBlur={save} />
          </div>
          <div>
            <label className={labelClass}>Trigger Mode</label>
            <select className={inputClass} value={triggerMode} onChange={(e) => { setTriggerMode(e.target.value as TransitionTriggerMode); setTimeout(save, 0); }}>
              <option value="manual">Manual</option>
              <option value="event">Event</option>
              <option value="deadline">Deadline</option>
              <option value="auto">Auto</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Button Label</label>
            <input type="text" className={inputClass} value={buttonLabel} onChange={(e) => setButtonLabel(e.target.value)} onBlur={save} placeholder="Custom button text" />
          </div>
          <div>
            <label className={labelClass}>Button Style</label>
            <select className={inputClass} value={buttonStyle} onChange={(e) => { setButtonStyle(e.target.value); setTimeout(save, 0); }}>
              <option value="default">Default</option>
              <option value="primary">Primary</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="danger">Danger</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Confirm Message</label>
            <input type="text" className={inputClass} value={confirmMessage} onChange={(e) => setConfirmMessage(e.target.value)} onBlur={save} placeholder="Optional confirmation dialog" />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={isCollaborative} onChange={(e) => { setIsCollaborative(e.target.checked); setTimeout(save, 0); }} className="rounded border-gray-300 dark:border-gray-600" />
              Collaborative (Approval Chain)
            </label>
          </div>
          {isCollaborative && (
            <div>
              <label className={labelClass}>On Rejection</label>
              <select className={inputClass} value={onRejection} onChange={(e) => { setOnRejection(e.target.value as any); setTimeout(save, 0); }}>
                <option value="block">Block</option>
                <option value="revert_to_previous">Revert to Previous</option>
                <option value="revert_to_status">Revert to Status</option>
              </select>
            </div>
          )}

          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            {!confirmDelete ? (
              <button type="button" onClick={() => setConfirmDelete(true)} className="w-full rounded-md border border-rose-300 px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 dark:border-rose-600 dark:text-rose-400 dark:hover:bg-rose-900/20">
                Delete Transition
              </button>
            ) : (
              <div className="flex gap-2">
                <button type="button" onClick={() => onDelete(transition.id)} className="flex-1 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-700">
                  Confirm
                </button>
                <button type="button" onClick={() => setConfirmDelete(false)} className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400">
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {activeSubTab === 'guards' && (
        <GuardListPanel
          transitionId={transition.id}
          guards={transition.guards ?? []}
          statuses={statuses}
          onRefresh={onRefresh}
        />
      )}

      {activeSubTab === 'validators' && (
        <ValidatorListPanel
          transitionId={transition.id}
          validators={transition.validators ?? []}
          onRefresh={onRefresh}
        />
      )}

      {activeSubTab === 'actions' && (
        <ActionListPanel
          transitionId={transition.id}
          actions={transition.actions ?? []}
          onRefresh={onRefresh}
        />
      )}

      {activeSubTab === 'forms' && (
        <FormListPanel
          transitionId={transition.id}
          forms={transition.forms ?? []}
          onRefresh={onRefresh}
        />
      )}

      {activeSubTab === 'approval' && isCollaborative && (
        <ApprovalChainBuilder
          transition={transition}
          statuses={statuses}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
};

export default TransitionPropertyPanel;
